<script setup lang="ts">
import { useFieldArray, useFormValues } from 'vee-validate'
import { computed, inject, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import * as apis from '/@src/client-portal/api'
import { formatDateString } from '/@src/utils/format'

const { t } = useI18n()
const api = new apis.ZoppmDraftApi()

const isReadOnly = inject<boolean>('isReadOnly')
const readOnlyResetKey = inject<string | number | undefined>('readOnlyResetKey')
const availablePodpisovatelia = ref([])
const vybranaOsoba = ref()
const { remove, push, fields } = useFieldArray('podpisovatelia')
onMounted(async () => {
  if (!isReadOnly) {
    availablePodpisovatelia.value = await api
      .fetchAvailablePodpisovatelListZoppmDraft(
        undefined,
        undefined,
        500,
        'menoUplne',
        'ASC'
      )
      .then((r) => r.data.content)
      .catch(console.error)
  }
})

const podpisovateliaAutocomplete = computed(() => {
  return (
    availablePodpisovatelia.value
      ?.filter(
        (podpisovatel) =>
          !fields.value?.find((field) => field.value?.osobaId === podpisovatel?.osobaId)
      )
      ?.map((osoba) => ({
        osobaId: osoba.osobaId,
        menoUplne: osoba.menoUplne,
      })) || []
  )
})

const getOsoba = (osobaId: string) => {
  return availablePodpisovatelia.value.find((osoba) => osoba.osobaId === osobaId)
}

const handleAdd = () => {
  if (vybranaOsoba.value) {
    push(getOsoba(vybranaOsoba.value.osobaId))
    vybranaOsoba.value = ''
  }
}
</script>

<template>
  <template v-if="!isReadOnly">
    <ValidationWrapper v-slot="{ errorMessage, ...slotProps }" name="podpisovatelia">
      <IDSKFieldCaption :error-message="errorMessage" variant="legend" />
      <ul class="govuk-list">
        <li v-for="(field, idx) in fields" :key="field.key">
          <ul v-bind="slotProps" class="govuk-list">
            <li class="govuk-!-font-weight-bold">{{ field.value?.menoUplne }}</li>
            <li>
              <Address :data="field.value?.adresaUplna" /> <br />
              <Address :data="field.value?.stat" />
            </li>
            <li v-if="field.value?.datumNarodenia">
              {{
                `${t('personal.birth')}: ${
                  formatDateString(field.value?.datumNarodenia) || ''
                }`
              }}
            </li>
          </ul>
          <IDSKLink class="mb-6" href="#" no-visited-state @click.prevent="remove(idx)">
            {{ t('action.remove-person') }}
          </IDSKLink>
          <IDSKDivider class="mt-5 mb-5" />
        </li>
      </ul>
    </ValidationWrapper>

    <div v-if="podpisovateliaAutocomplete?.length > 0" class="autocomplete--with-button">
      <IDSKAutocomplete
        id="podpisovatelia-autocomplete"
        :key="podpisovateliaAutocomplete?.length"
        v-model="vybranaOsoba"
        placeholder=""
        :label="t('search.label')"
        :options="podpisovateliaAutocomplete"
        option-object-value="osobaId"
        option-object-label="menoUplne"
      />
      <IDSKButton
        variant="secondary"
        :data-test-id="`zoppm-podpisovatelia-pridat`"
        @click="handleAdd"
      >
        {{ t('globalne.pridat') }}
      </IDSKButton>
    </div>
  </template>

  <div v-else class="autocomplete--with-button">
    <IDSKAutocomplete
      id="podpisovatelia-autocomplete-read-only"
      :key="readOnlyResetKey"
      placeholder=""
      :label="t('search.label')"
      :options="[]"
    />
    <IDSKButton variant="secondary" :data-test-id="`zoppm-podpisovatelia-pridat`">
      {{ t('globalne.pridat') }}
    </IDSKButton>
  </div>
</template>

<i18n lang="yaml">
sk:
  title:
    representers: 'Osoby zastupujúce subjekt'
  info1: 'Zo všetkých štatutárnych orgánov žiadateľa alebo osôb s oprávením konať za právnickú osobu uvedených nižšie, prosím, vyberte iba tie, od ktorých je z hľadiska legislatívnych podmienok požadovaný podpis čestného vyhlásenia a žiadosti.'
  info2: 'V prípade, že je k signatárom potrebné doplniť aj inú osobu, jednoducho ju pridajte zo zoznamu osôb subjektu. Vybrané osoby môžu dokumenty podpisovať až po záväznom zaevidovaní žiadosti do ISPO a stiahnutí vygenerovaného súboru (.pdf).'
  info3: 'Pokiaľ štatutárny orgán žiadateľa zastupujete na základe poverenia v tomto úkone, je potrebné, aby ste k žiadosti o pridelenie prostriedkov mechanizmu priložili splnomocnenie (pozn. na formulári žiadosti zostávajú identifikačné údaje štatutárneho orgánu žiadateľa).'
  personal:
    birth: 'Dátum narodenia'
  action:
    remove-person: Odobrať osobu
  search:
    label: Vyhľadajte osobu z konta
  message:
    no-options: Žiadne osoby
</i18n>
